import * as React from 'react';
import { OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import './bloodwork-ref-range-analysis.css';
import { ReferenceRangeChart } from '../charts/ReferenceRangeChart';
import { EGFRReferenceRangeChart } from '../charts/EGFRReferenceRangeChart';
import {
  Bloodwork,
  Section,
  PanelMarker,
  MenstrualPhase,
  CustomTextMarker,
} from '../../models';
import { RDWReferenceRangeChart } from '../charts/RDWReferenceRangeChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCollectionDate } from '../../helpers/previousOnlyMarkers';

type Props = {
  title: string;
  section: Section;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  panelMarkers: PanelMarker[];
  /**
   * This section's practitioner-typed markers whose result is WORDS.
   *
   * Printed as plain rows beneath the bar graphs, in the same table, so they
   * line up with them and travel with them into the PDF. Deliberately a
   * separate prop rather than more PanelMarkers: there is no value to plot and
   * no range to plot it against (see CustomTextMarker).
   */
  textMarkers?: CustomTextMarker[];
  // Optional: sections with no bibliography anchor (e.g. the practitioner's own
  // custom markers) omit it, and the "learn more" affordance is skipped.
  infoLink?: string;
  // PDF export rendering. The external-link ("learn more") icon next to each
  // marker name is interactive and only useful on screen — it never renders in
  // the exported PDF — so we omit it here, which also lets the marker name sit
  // flush-right with the value/unit beneath it.
  forPdf?: boolean;
};

type State = {
  showRefRangeModal: boolean;
};

export class ReferenceRangeAnalysis extends React.Component<Props, {}> {
  state: State = {
    showRefRangeModal: false,
  };

  showRefRangeModal = () => {
    const { infoLink } = this.props;
    if (infoLink) {
      window.open(infoLink);
    } else {
      this.setState({
        showRefRangeModal: true,
      });
    }
  };

  onHideRefRangeModal = () => {
    this.setState({
      showRefRangeModal: false,
    });
  };

  labCorpInfoTooltip = (panelMarker: PanelMarker) => {
    // tslint:disable-next-line:max-line-length
    const labCorpInfoTooltip =
      'Click this icon to learn more about how the reference ranges for this marker were determined.';
    return <Tooltip id="modal-tooltip">{labCorpInfoTooltip}</Tooltip>;
  };

  displayChart(panelMarker: PanelMarker, index: number) {
    console.log({ eGFR: panelMarker });
    let chart = (
      <ReferenceRangeChart
        panelMarker={panelMarker}
        chartContainerClass="reference-range-chart-container"
        showPlotBandLabels={index === 1}
      />
    );
    if (panelMarker.isEGFR() || panelMarker.field.useEGFRChart) {
      chart = (
        <EGFRReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
        />
      );
    } else if (panelMarker.field.useRDWChart) {
      chart = (
        <RDWReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
        />
      );
    }
    return chart;
  }

  openWindow(pm?: PanelMarker) {
    if (pm) {
      window.open(pm.field.labCorpLink);
    } else if (this.props.infoLink) {
      window.open(this.props.infoLink);
    }
  }

  // `table-row`, never `block`: a block <tr> is not a row, so the browser wraps
  // it in an anonymous cell that lands in the table's FIRST column, and every
  // real cell of a text row then sizes against that column. That is how a
  // two-word result ended up pinned to the panel's right edge, its marker name
  // 500px right of every charted marker's name, while the bar graphs lost the
  // width the (squeezed) second column was holding. Rows share one column model
  // or the labels do not line up.
  showChart(index: number): string {
    return index === 0 ? 'none' : 'table-row';
  }

  // A "previous-only" marker is blank in the current test but carries a
  // previous-test value (see BloodworkAnalysis.getPreviousOnlyPanelMarkers).
  // Its chart shows just the previous (blue) blood drop, so the value/label
  // beneath it must come from the previous marker, not the (empty) current one.
  isPreviousOnly(pm: PanelMarker): boolean {
    return (
      !pm.hasValue() &&
      !!pm.previousPanelMarker &&
      pm.previousPanelMarker.hasValue()
    );
  }

  formatMenstrualPhase(phase: MenstrualPhase) {
    return phase === MenstrualPhase.NONE
      ? `${MenstrualPhase.NONE} (children's ranges)`
      : phase;
  }

  menstrualPhase() {
    const { section, bloodwork, previousBloodwork } = this.props;
    if (section !== Section.SEX_HORMONE) {
      return null;
    }
    const menstrualPhase = bloodwork.menstrualPhase;
    if (!menstrualPhase) {
      return null;
    }
    const previousMenstrualPhase = previousBloodwork && previousBloodwork.menstrualPhase;
    return (
      // Same width guard as the notes below: this line is longer on one line
      // than the bar-graph table, and the panel's card-body sizes itself to its
      // widest content.
      <div className="ref-range-menstrual-phase">
        <span style={{ marginLeft: '20px', fontWeight: 'bold' }}>
          Menstrual Phase
        </span>
        <span style={{ marginLeft: '8px' }}>
          {this.formatMenstrualPhase(menstrualPhase)}
        </span>
        {previousMenstrualPhase && (
          <>
            <span style={{ marginLeft: '24px', fontWeight: 'bold' }}>
              Menstrual Phase for Previous Collection Date:
            </span>
            <span style={{ marginLeft: '8px' }}>
              {this.formatMenstrualPhase(previousMenstrualPhase)}
            </span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="previous-menstrual-phase-tooltip">
                  <div>
                    The previous blood drop is plotted at its position within the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} reference range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    Even though this chart shows the
                    {' '}{this.formatMenstrualPhase(menstrualPhase)} bands, judge the
                    previous value only by the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    For example, a drop at the low end of optimal is at the low end
                    of optimal for {this.formatMenstrualPhase(previousMenstrualPhase)},
                    not {this.formatMenstrualPhase(menstrualPhase)}.
                  </div>
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                icon="info-circle"
                style={{ marginLeft: '8px', color: 'var(--primary)', cursor: 'pointer' }}
              />
            </OverlayTrigger>
          </>
        )}
      </div>
    );
  }

  /**
   * The rule above or below the block of text results.
   *
   * A row of its own rather than a border on the first and last text rows,
   * because the space this puts between the last chart's axis numbers and the
   * line has to sit ABOVE the line — and padding on a cell is inside its own
   * border, which would put the space under it.
   */
  textRule(edge: 'top' | 'bottom') {
    return (
      <tr key={`text-rule-${edge}`}>
        <td
          colSpan={2}
          className={`ref-range-text-rule ref-range-text-rule-${edge}`}
        />
      </tr>
    );
  }

  /**
   * One practitioner-typed marker whose result is words.
   *
   * Rendered into the SAME table as the bar graphs rather than a table of its
   * own, so the marker names line up down one column: a urinalysis sitting
   * under a chemistry panel in a second table with its own column widths reads
   * as a different document stapled to the bottom of this one.
   *
   * Across the cell where a bar would be, the result is laid out in labeled
   * columns — Result, then Expected Result when the panel has any expectation
   * to print. The columns are fixed widths rather than content-sized, so they
   * line up down the panel the same way the names do; a row with no expectation
   * of its own simply leaves the last column empty rather than closing the gap
   * and knocking every row below it out of alignment.
   */
  textRow(
    textMarker: CustomTextMarker,
    key: string,
    showAsterisk: boolean,
    showExpected: boolean,
  ) {
    const previousOnly = textMarker.isPreviousOnly();
    const differs = textMarker.differsFromExpected();
    const previousCollectionDate = formatCollectionDate(
      this.props.previousBloodwork?.collectionDate,
    );
    // Only worth printing when it is DIFFERENT. The same word on both tests is
    // the normal case and repeating it down a fourteen-line panel is noise.
    const previousText = (textMarker.previousText || '').trim();
    const changed =
      !previousOnly &&
      !!previousText &&
      previousText.toLowerCase() !== textMarker.displayText().toLowerCase();
    const expected = (textMarker.expected || '').trim();
    return (
      <tr key={key}>
        <td className="ref-range-category ref-range-category-wrap">
          {showAsterisk && previousOnly && (
            <span
              className="ref-range-previous-only-asterisk"
              title="Previous test only — see the note below the chart."
            >
              *
            </span>
          )}
          {textMarker.label}
        </td>
        <td className="ref-range-text-result-cell">
          <div
            className={
              showExpected
                ? 'ref-range-text-grid'
                : 'ref-range-text-grid ref-range-text-grid-result-only'
            }
          >
            <span className="ref-range-text-column-label">Result</span>
            <span className="ref-range-text-column-value">
              <span
                className={
                  differs
                    ? 'ref-range-text-result ref-range-text-result-differs'
                    : 'ref-range-text-result'
                }
              >
                {textMarker.displayText()}
              </span>
              {changed ? (
                <span className="ref-range-text-previous">
                  previous
                  {previousCollectionDate ? ` (${previousCollectionDate})` : ''}:{' '}
                  {previousText}
                </span>
              ) : null}
            </span>
            {/* Stated rather than implied by the color alone: the color is not
                available to a reader printing in grayscale, and a named column
                says what a bare second word next to the result never did. */}
            {showExpected ? (
              <>
                {/* Both spans render even with nothing to say, because they ARE
                    the last two grid columns — drop one and the row below
                    reflows into its place. The LABEL goes when the value does:
                    it names a column, and naming an empty cell says nothing. */}
                <span className="ref-range-text-column-label">
                  {expected ? 'Expected Result' : ''}
                </span>
                <span className="ref-range-text-column-value ref-range-text-expected">
                  {expected}
                </span>
              </>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  render() {
    const { panelMarkers } = this.props;
    const textMarkers = this.props.textMarkers || [];
    // A section can be made ENTIRELY of text markers — a urinalysis panel is
    // exactly that — so an empty chart list is no longer an empty section.
    const hasCharts = !!panelMarkers && panelMarkers.length > 0;
    if (!hasCharts && !textMarkers.length) {
      return null;
    }

    // This is a hack because the blood drop on the first blood drop is always misaligned.
    // We take the first marker and place it at the beginning of the array and then
    // we don't display it.
    if (hasCharts) {
      const fakePanelMarker: PanelMarker = panelMarkers[0];
      panelMarkers.unshift(fakePanelMarker);
    }

    // Whether any row is a "previous-only" marker — drives the footnote below
    // the chart that explains the asterisk next to those marker names.
    //
    // When EVERY row is previous-only the section has no results in this
    // collection at all, and a per-row mark distinguishes nothing. That case
    // gets one line above the table instead, naming the collection the bars
    // come from, and the asterisks and their footnote drop out.
    const allPreviousOnly =
      panelMarkers.every(pm => this.isPreviousOnly(pm)) &&
      textMarkers.every(textMarker => textMarker.isPreviousOnly());
    const hasPreviousOnly =
      !allPreviousOnly &&
      (panelMarkers.some(pm => this.isPreviousOnly(pm)) ||
        textMarkers.some(textMarker => textMarker.isPreviousOnly()));

    // The Expected Result columns are a property of the PANEL, not of a row:
    // dropped when nothing in the panel states an expectation (the common case
    // — most results are just words), kept for every row once one does, so a
    // row with no expectation of its own leaves an empty column instead of
    // reflowing the ones above and below it.
    const hasExpected = textMarkers.some(
      textMarker => !!(textMarker.expected || '').trim(),
    );

    // Both the line and the footnote name the collection those bars came from.
    // Undated, either one reads as a claim about the test the report is headed
    // with, which is the one collection it is definitely not about.
    const previousCollectionDate = formatCollectionDate(
      this.props.previousBloodwork?.collectionDate,
    );

    return (
      <div>
        {this.menstrualPhase()}
        {allPreviousOnly && (
          <div className="ref-range-previous-collection-note">
            Every marker below is from the previous collection
            {previousCollectionDate ? ` (${previousCollectionDate})` : ''}.
            This collection has no result for any of them
            {hasCharts
              ? ', so each chart shows just the previous (blue) blood drop.'
              : '.'}
          </div>
        )}
        <Table className="labsmartz-chart-table">
          <tbody>
            {(hasCharts ? panelMarkers : []).map((pm: PanelMarker, index: number) => {
              return (
                <tr key={index} style={{ display: this.showChart(index) }}>
                  <td className={`ref-range-category ${pm.field.wrapChartLabel ? 'ref-range-category-wrap' : 'text-nowrap'}`}>
                    {!allPreviousOnly && this.isPreviousOnly(pm) && (
                      <span
                        className="ref-range-previous-only-asterisk"
                        title="Previous test only — see the note below the chart."
                      >
                        *
                      </span>
                    )}
                    {pm.field.getChartLabel()}
                    {!this.props.forPdf && (
                      <>
                        &nbsp;&nbsp;
                        {pm.field.labCorpLink ? (
                          <OverlayTrigger overlay={this.labCorpInfoTooltip(pm)}>
                            <FontAwesomeIcon
                              icon="external-link-alt"
                              onClick={() => {
                                this.openWindow(pm);
                              }}
                            />
                          </OverlayTrigger>
                        ) : null}
                      </>
                    )}
                    <br />
                    <span className="ref-range-category-value">
                      {this.isPreviousOnly(pm)
                        ? pm.previousPanelMarker!.displayValue()
                        : pm.displayValue()}{' '}
                      {pm.displayUnits(true)}
                    </span>
                  </td>
                  <td>{this.displayChart(pm, index)}</td>
                </tr>
              );
            })}
            {/* Under the bar graphs, in entry order. A marker with no number
                cannot be sorted among ones that have them, and grouping them
                at the foot of the panel is also how a lab report prints a
                urinalysis: the measurements, then the observations. */}
            {textMarkers.length > 0 && this.textRule('top')}
            {textMarkers.map((textMarker, index) =>
              this.textRow(
                textMarker,
                `text-${index}`,
                !allPreviousOnly,
                hasExpected,
              ),
            )}
            {textMarkers.length > 0 && this.textRule('bottom')}
          </tbody>
        </Table>
        {/* One line in the panel, which is what a footnote should be. "only"
            already carries "this collection has no value for it", so spelling
            that out cost a second line and said nothing new. Budget for edits:
            about 110 characters including the date, measured at this panel's
            fixed width — past that it wraps. */}
        {hasPreviousOnly && (
          <div className="ref-range-previous-only-note">
            <span className="ref-range-previous-only-asterisk">*</span>
            {' '}Result from the previous collection
            {previousCollectionDate ? ` (${previousCollectionDate})` : ''} only
            {hasCharts ? ', so the chart shows just its blue blood drop.' : '.'}
          </div>
        )}
      </div>
    );
  }
}
